import { Head, useAuth } from "zudoku/components";
import { useState, useEffect } from "react";

interface QuotaData {
  used: number;
  limit: number;
  remaining: number;
}

export const SubscriptionManagementPage = () => {
  const auth = useAuth();
  const [quota, setQuota] = useState<QuotaData | null>(null);
  const [quotaLoading, setQuotaLoading] = useState(false);

  const STRIPE_PORTAL_URL = "https://billing.stripe.com/p/login/aFa7sKb86cMQ57G2UId7q00";

  // Extract user data from token
  const getUserData = () => {
    if (!auth.isAuthenticated)
      return { isSuperAdmin: false, userTier: "unknown", permission: "unknown" };

    const token =
      (auth as any).providerData?.accessToken ||
      (auth.user as any)?.access_token;
    if (!token) return { isSuperAdmin: false, userTier: "unknown", permission: "unknown" };

    try {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      const payload = JSON.parse(jsonPayload);

      const userTier =
        payload["https://api.securitydatapoint.com/tier"] ||
        payload.tier ||
        "unknown";
      const permission =
        payload["https://api.securitydatapoint.com/permission"] ||
        payload.permission ||
        "standard";

      return {
        isSuperAdmin: permission === "superadmin",
        userTier,
        permission,
      };
    } catch {
      return { isSuperAdmin: false, userTier: "unknown", permission: "unknown" };
    }
  };

  const { isSuperAdmin, userTier, permission } = getUserData();

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    const fetchQuota = async () => {
      setQuotaLoading(true);
      try {
        const token =
          (auth as any).providerData?.accessToken ||
          (auth.user as any)?.access_token;
        if (!token) return;

        const response = await fetch(
          "https://api.securitydatapoint.com/api/v1/tickers/quota",
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.ok) {
          setQuota(await response.json());
        }
      } catch {
        // quota stays null — render gracefully
      } finally {
        setQuotaLoading(false);
      }
    };

    fetchQuota();
  }, [auth.isAuthenticated]);

  if (!auth.isAuthenticated) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <Head>
          <title>Subscription Management</title>
        </Head>
        <h1>Subscription Management</h1>
        <p>Please log in to access this page.</p>
      </div>
    );
  }

  const tierColor: Record<string, string> = {
    enterprise: "#7c3aed",
    scale: "#2563eb",
    core: "#059669",
    "data sampler": "#d97706",
  };
  const tierBadgeColor = tierColor[userTier.toLowerCase()] ?? "#6b7280";

  return (
    <div style={{ padding: "40px", maxWidth: "1200px", margin: "0 auto" }}>
      <Head>
        <title>Subscription Management</title>
      </Head>

      <h1 style={{ marginBottom: "10px" }}>Subscription Management</h1>
      <p style={{ color: "#6b7280", marginBottom: "30px" }}>
        Your current plan details and subscription management.
      </p>

      {/* ── Plan Overview card (all authenticated users) ── */}
      <div
        style={{
          backgroundColor: "#f9fafb",
          padding: "28px 30px",
          borderRadius: "8px",
          border: "1px solid #e5e7eb",
          marginBottom: "24px",
        }}
      >
        <h2 style={{ fontSize: "18px", marginBottom: "20px", color: "#111827", textAlign: "left" }}>
          Plan Overview
        </h2>
        <div style={{ display: "flex", gap: "32px", flexWrap: "wrap", justifyContent: "flex-start", alignItems: "center" }}>
          {/* Tier */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Tier
            </div>
            <span
              style={{
                display: "inline-block",
                backgroundColor: tierBadgeColor,
                color: "#fff",
                padding: "4px 14px",
                borderRadius: "9999px",
                fontSize: "14px",
                fontWeight: "600",
                textTransform: "capitalize",
              }}
            >
              {userTier}
            </span>
          </div>

          {/* Permission */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Permission
            </div>
            <span
              style={{
                display: "inline-block",
                backgroundColor: isSuperAdmin ? "#1e40af" : "#374151",
                color: "#fff",
                padding: "4px 14px",
                borderRadius: "9999px",
                fontSize: "14px",
                fontWeight: "600",
                textTransform: "capitalize",
              }}
            >
              {isSuperAdmin ? "Superadmin" : permission}
            </span>
          </div>

          {/* Monthly companies limit */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: "12px", color: "#6b7280", marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              Monthly Companies Limit
            </div>
            {quotaLoading ? (
              <span style={{ fontSize: "14px", color: "#9ca3af" }}>Loading…</span>
            ) : quota ? (
              <span style={{ fontSize: "20px", fontWeight: "700", color: "#6b7280" }}>
                {quota.used.toLocaleString()}
                <span style={{ fontSize: "20px", fontWeight: "700", color: "#6b7280" }}>
                  {" "}/ {quota.limit.toLocaleString()}
                </span>
                <span style={{ fontSize: "20px", color: "#111827", marginLeft: "10px" }}>
                  ({quota.remaining.toLocaleString()} remaining)
                </span>
              </span>
            ) : (
              <span style={{ fontSize: "14px", color: "#9ca3af" }}>—</span>
            )}
          </div>
        </div>
      </div>

      {/* ── Stripe portal (superadmins only) ── */}
      {isSuperAdmin && (
        <>
          <div
            style={{
              backgroundColor: "#f9fafb",
              padding: "30px",
              borderRadius: "8px",
              border: "1px solid #e5e7eb",
              marginBottom: "24px",
            }}
          >
            <h2 style={{ fontSize: "20px", marginBottom: "10px" }}>
              Billing & Subscription Portal
            </h2>
            <p style={{ color: "#6b7280", marginBottom: "20px" }}>
              Access the Stripe customer portal to view and manage your
              subscription plan, update payment methods, download invoices, and
              cancel or upgrade your plan.
            </p>

            <a
              href={STRIPE_PORTAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "inline-block",
                backgroundColor: "#2563eb",
                color: "white",
                padding: "12px 24px",
                border: "none",
                borderRadius: "6px",
                fontSize: "16px",
                fontWeight: "500",
                cursor: "pointer",
                textDecoration: "none",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) =>
                (e.currentTarget.style.backgroundColor = "#1d4ed8")
              }
              onMouseOut={(e) =>
                (e.currentTarget.style.backgroundColor = "#2563eb")
              }
            >
              Open Subscription Portal →
            </a>
          </div>

          <div
            style={{
              backgroundColor: "#eff6ff",
              padding: "20px",
              borderRadius: "8px",
              border: "1px solid #bfdbfe",
            }}
          >
            <h3 style={{ fontSize: "16px", marginBottom: "8px", color: "#1e40af" }}>
              What you can do in the portal
            </h3>
            <ul
              style={{
                color: "#374151",
                margin: 0,
                paddingLeft: "20px",
                lineHeight: "1.8",
              }}
            >
              <li>View your current subscription plan and usage</li>
              <li>Upgrade or downgrade your plan</li>
              <li>Update payment method and billing information</li>
              <li>Download past invoices and receipts</li>
              <li>Cancel your subscription</li>
            </ul>
          </div>
        </>
      )}


    </div>
  );
};
